import type { PriceImportRow } from 'apis/price-imports';
import { scoreProductName } from 'pages/inquiry-processor/utils/rank-catalog-suggestions';

/**
 * What committing this run would CREATE, deduplicated across rows.
 *
 * Proposals live on each row rather than in their own table, and several rows
 * routinely propose the same thing — the real DEEP list proposed twelve
 * families across twenty lines. Showing them per row would ask the operator
 * twenty questions with twelve answers, so they are folded here by the same key
 * the backend's commit preamble folds by: lowercased name.
 */

export interface FamilyProposal {
  key: string;
  universalName: string;
  catalogCategoryId: number | null;
  /** The rows that would be blocked if this family is not resolved. */
  rowIds: number[];
  rowNumbers: number[];
  /** The category text the supplier's file carried, for context when asking. */
  sourceCategoryText: string | null;
}

export interface NamedProposal {
  key: string;
  label: string;
  rowIds: number[];
  rowNumbers: number[];
}

export interface AggregatedProposals {
  /** Ordered as the commit preamble creates them: values, brands, families. */
  dictionaryValues: NamedProposal[];
  brands: NamedProposal[];
  families: FamilyProposal[];
  /** Rows that would create a variant. Not deduplicated: each row is one variant. */
  variantRowIds: number[];
  /** Families still missing a category — these BLOCK the rows behind them. */
  blockedFamilies: FamilyProposal[];
  total: number;
}

const push = (map: Map<string, NamedProposal>, key: string, label: string, row: PriceImportRow) => {
  const existing = map.get(key) ?? { key, label, rowIds: [], rowNumbers: [] };
  existing.rowIds.push(row.id);
  existing.rowNumbers.push(row.rowNumber);
  map.set(key, existing);
};

export function aggregateProposals(rows: PriceImportRow[]): AggregatedProposals {
  const dictionaryValues = new Map<string, NamedProposal>();
  const brands = new Map<string, NamedProposal>();
  const families = new Map<string, FamilyProposal>();
  const variantRowIds: number[] = [];

  rows.forEach((row) => {
    // A committed row's proposal has already been applied.
    if (!row.proposal || row.committedAt) return;

    (row.proposal.dictionaryValues ?? []).forEach((value) => {
      push(dictionaryValues, `${value.type}:${value.value}`.toLowerCase(), `${value.type}: ${value.value}`, row);
    });

    if (row.proposal.brand?.name) {
      push(brands, row.proposal.brand.name.trim().toLowerCase(), row.proposal.brand.name, row);
    }

    const family = row.proposal.family;
    if (family?.universalName) {
      const key = family.universalName.trim().toLowerCase();
      const existing = families.get(key) ?? {
        key,
        universalName: family.universalName.trim(),
        catalogCategoryId: family.catalogCategoryId,
        rowIds: [],
        rowNumbers: [],
        sourceCategoryText: row.canonical.catalog_category ?? null,
      };

      // Any row already carrying a category answers for the whole group.
      if (existing.catalogCategoryId == null && family.catalogCategoryId != null) {
        existing.catalogCategoryId = family.catalogCategoryId;
      }

      existing.rowIds.push(row.id);
      existing.rowNumbers.push(row.rowNumber);
      families.set(key, existing);
    }

    if (row.proposal.master) variantRowIds.push(row.id);
  });

  const familyList = [...families.values()].sort((a, b) => a.universalName.localeCompare(b.universalName));

  return {
    dictionaryValues: [...dictionaryValues.values()].sort((a, b) => a.label.localeCompare(b.label)),
    brands: [...brands.values()].sort((a, b) => a.label.localeCompare(b.label)),
    families: familyList,
    variantRowIds,
    blockedFamilies: familyList.filter((family) => family.catalogCategoryId == null),
    total: dictionaryValues.size + brands.size + families.size + variantRowIds.length,
  };
}

/**
 * Proposed families whose name closely resembles an existing one.
 *
 * The cheap guard against catalogue pollution: proposing "Fish Fingers" while
 * "Fish Finger" already exists is how a catalogue grows two families that can
 * never match each other again.
 *
 * Scored with the SAME function the quote matcher uses, so the warning agrees
 * with the matcher's own notion of a similar name — and inherits its
 * singularizer, which is what makes the plural case work at all. A hand-rolled
 * token overlap misses "Fingers" vs "Finger" entirely.
 *
 * It warns; it never blocks. Plenty of legitimately similar families exist.
 */
export const NEAR_DUPLICATE_SCORE = 60;

export function findNearDuplicates(proposedName: string, existingNames: string[]): string[] {
  return existingNames.filter((name) => scoreProductName(proposedName, name) >= NEAR_DUPLICATE_SCORE);
}
